import { useRef, useState, useEffect, type ChangeEvent } from 'react'
import { BUSINESS_SETUP_MAX_FILES, BUSINESS_SETUP_MAX_FILE_BYTES, BusinessSetupDocumentsSchema, type BusinessSetupDocument } from '@tenfour/contracts'
import { api } from '@/lib/api'

export function useBusinessDocuments(documents: BusinessSetupDocument[], onChange: (next: BusinessSetupDocument[]) => void | Promise<void>) {
  const [progress, setProgress] = useState('')
  const [error, setError] = useState('')
  const controller = useRef<AbortController | null>(null)
  useEffect(() => () => controller.current?.abort(), [])
  async function select(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (!files.length || controller.current) return
    setError('')
    if (files.length + documents.length > BUSINESS_SETUP_MAX_FILES) { setError('Choose up to 20 documents in total. Split larger folders into a smaller selection.'); return }
    const abort = new AbortController()
    controller.current = abort
    const next = [...documents], failures: string[] = []
    try {
      for (const [index, file] of files.entries()) {
        setProgress(`Reading document ${index + 1} of ${files.length}…`)
        const name = file.webkitRelativePath || file.name
        try {
          if (file.size > BUSINESS_SETUP_MAX_FILE_BYTES) throw new Error('Each document must be 5 MB or smaller.')
          const body = new FormData(); body.append('file', file); body.append('path', name)
          const document = await api<BusinessSetupDocument>('/business-setup/document', { method: 'POST', body, signal: abort.signal })
          const existing = next.findIndex(doc => doc.name === document.name)
          const candidate = existing < 0 ? [...next, document] : next.map((doc, i) => i === existing ? document : doc)
          const checked = BusinessSetupDocumentsSchema.safeParse(candidate)
          if (!checked.success) throw new Error('Use fewer or shorter documents (40,000 characters total).')
          next.splice(0, next.length, ...checked.data)
        } catch (err) {
          if (abort.signal.aborted) return
          failures.push(`${name}: ${err instanceof Error ? err.message : 'Could not read this file.'}`)
        }
      }
      if (next.length || documents.length) await onChange(next)
      setError(failures.join('\n'))
    } catch (err) { if (!abort.signal.aborted) setError(err instanceof Error ? err.message : 'Could not save documents. Please select them again.') }
    finally { controller.current = null; if (!abort.signal.aborted) setProgress('') }
  }
  async function remove(event: React.MouseEvent<HTMLButtonElement>) {
    if (controller.current) return
    setError(''); setProgress('Updating documents…')
    try { await onChange(documents.filter(doc => doc.name !== event.currentTarget.value)) }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not remove the document. Try again.') }
    finally { setProgress('') }
  }
  return { progress, error, select, remove }
}
